import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Globe, ChevronDown, Check } from 'lucide-react';

interface Language {
  code: string;
  label: string;
  urlCode?: string; // 如果 URL 里的代码和 i18n code 不一样（比如 zh-TW -> tw）
}

const languages: Language[] = [
  { code: 'en', label: 'English' },
  { code: 'ja', label: '日本語' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'ar', label: 'العربية' },
  { code: 'pt', label: 'Português' },
  { code: 'id', label: 'Bahasa Indonesia' },
  { code: 'ko', label: '한국어' },
  { code: 'vi', label: 'Tiếng Việt' },
  { code: 'ru', label: 'Русский' },
  { code: 'zh', label: '简体中文' },
  { code: 'zh-TW', label: '繁體中文', urlCode: 'tw' },
];

export const LanguageSelector: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 获取当前显示的语言标签
  const currentLangCode = i18n.language;
  const currentLang = languages.find(l => l.code === currentLangCode) || languages[0];

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageChange = (lang: Language) => {
    setIsOpen(false);
    
    if (lang.code === 'en') {
      navigate('/');
      return;
    }

    const urlLang = lang.urlCode || lang.code;
    navigate(`/${urlLang}`);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-lg 
          transition-all duration-200 
          font-medium text-sm
          ${isOpen 
            ? 'bg-cyan-50 text-cyan-700 ring-2 ring-cyan-100' 
            : 'hover:bg-slate-100 text-slate-600 hover:text-slate-900'
          }
        `}
      >
        <Globe className="w-4 h-4" />
        <span>{currentLang.label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl shadow-slate-200/50 border border-slate-100 py-2 z-50 animate-in fade-in zoom-in-95 duration-200 origin-top-right">
          <div className="px-2 pb-2 mb-2 border-b border-slate-50">
            <span className="text-xs font-semibold text-slate-400 px-2">Select Language</span>
          </div>
          <div className="flex flex-col gap-0.5 px-1">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang)}
                className={`
                  flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors w-full text-left
                  ${currentLangCode === lang.code 
                    ? 'bg-cyan-50 text-cyan-700 font-medium' 
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }
                `}
              >
                <span>{lang.label}</span>
                {currentLangCode === lang.code && (
                  <Check className="w-3.5 h-3.5 text-cyan-600" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

